import { apiService } from '../../services/api';
import type {
  DeliveryResult,
  MailCheck,
  MailSettings,
  NotificationLogView,
  SubscriptionView,
  UpdateSubscriptionInput,
} from '../../types/notifications';

const BASE = '/api/notifications';

/** The backend on this server predates `/api/notifications` — settings cannot fix that. */
export class NotificationsUnavailableError extends Error {
  constructor(message = 'Email notifications are not available on this server yet.') {
    super(message);
    this.name = 'NotificationsUnavailableError';
  }
}

/** Raised when the caller may not read or write mail settings — an admin can */
export class NotificationsForbiddenError extends Error {
  constructor(message = 'You do not have permission to do that') {
    super(message);
    this.name = 'NotificationsForbiddenError';
  }
}

/** One short line a person can act on, never a raw stack or a bare status code */
export function notificationsErrorMessage(error: any): string {
  if (error instanceof NotificationsUnavailableError) return error.message;
  if (error instanceof NotificationsForbiddenError) return error.message;
  const data = error?.response?.data;
  // This API answers either { message } or { error: { message } }
  const body = data?.message ?? data?.error?.message;
  const raw = Array.isArray(body) ? body.join(', ') : body || error?.message || '';
  const text = String(raw).trim();
  const status = error?.response?.status;
  if (status === 401) return 'Your session has expired — sign in again';
  if (status === 403) return 'You do not have permission to do that';
  if (/network error|failed to fetch|load failed/i.test(text)) return 'Cannot reach the server — check your connection';
  if (/econnrefused|enotfound|etimedout|ehostunreach/i.test(text)) return `The mail server refused the connection: ${text}`;
  if (/invalid login|535|authentication failed/i.test(text)) return `The mail server rejected the sign-in: ${text}`;
  if (/not configured|no mail|smtp.*(missing|disabled)/i.test(text)) return 'Email is not configured on this server';
  if (status >= 500) return text && text.length <= 140 ? `Server error: ${text}` : 'The server could not complete that — try again';
  return text || 'Something went wrong';
}

/** A 404 on one of these endpoints means the module is not deployed, not "nothing found" */
function asUnavailable(error: any): never {
  if (error?.response?.status === 404) throw new NotificationsUnavailableError();
  if (error?.response?.status === 403) throw new NotificationsForbiddenError();
  throw error;
}

/* ------------------------------------------------------------------ */
/* A person's own preferences                                          */
/* ------------------------------------------------------------------ */

export async function fetchPreferences(): Promise<SubscriptionView[]> {
  try {
    const res = await apiService.get<SubscriptionView[]>(`${BASE}/preferences`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function savePreference(input: UpdateSubscriptionInput): Promise<SubscriptionView[]> {
  const res = await apiService.patch<SubscriptionView[]>(`${BASE}/preferences`, input);
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Quiet hours are one setting to a person but one column per subscription on the
 * server. The endpoint takes the whole set at once, so the window can never land
 * on half the kinds.
 */
export async function savePreferences(inputs: UpdateSubscriptionInput[]): Promise<SubscriptionView[]> {
  if (inputs.length === 0) return fetchPreferences();
  if (inputs.length === 1) return savePreference(inputs[0]);
  const res = await apiService.patch<SubscriptionView[]>(`${BASE}/preferences`, { items: inputs });
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchHistory(): Promise<NotificationLogView[]> {
  try {
    const res = await apiService.get<NotificationLogView[]>(`${BASE}/history`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

/* ------------------------------------------------------------------ */
/* Delivery, for admins                                                */
/* ------------------------------------------------------------------ */

export async function fetchMailSettings(): Promise<MailSettings> {
  try {
    const res = await apiService.get<MailSettings>(`${BASE}/mail`);
    return res.data;
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function saveMailSettings(input: Partial<MailSettings>): Promise<MailSettings> {
  const res = await apiService.patch<MailSettings>(`${BASE}/mail`, input);
  return res.data;
}

/** Asks the server to actually talk to the mail host; the failure reason is the point */
export async function sendTestMessage(): Promise<MailCheck> {
  const res = await apiService.post<MailCheck>(`${BASE}/mail/test`, {});
  return res.data;
}

/** Runs a brief now, so an admin can see exactly what goes out */
export async function runDigest(kind: 'weekly_digest' | 'daily_digest' = 'weekly_digest'): Promise<DeliveryResult> {
  const res = await apiService.post<DeliveryResult>(`${BASE}/digest/run`, { kind });
  return res.data;
}
